import type { ColDef } from 'ag-grid-community'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import { getPayableRegistrationTypeLabel } from '@sportninja/common/utils/billing'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import SetColumnFilter from 'src/components/ts-components/DataGrid/SetColumnFilter'
import ActionsCell from '../components/cells/ActionsCell'
import CompetitionCell from '../components/cells/CompetitionCell'
import NameCell from '../components/cells/NameCell'
import PaidCell from '../components/cells/PaidCell'
import PaidDateCell from '../components/cells/PaidDateCell'
import StatusCell from '../components/cells/StatusCell'
import { REGISTRATION_STATUSES } from '../constants/registrationStatuses'
import { formatRegistrationMoney } from '../utils/formatRegistrationMoney'
import {
  compareRegistrationDates,
  compareRegistrationNames,
  getCompetitionFilterOptions,
  getOutstandingAmount,
  getRegistrationCountry,
} from '../utils/registrationRows'

export const REGISTRATION_CELL_CLASS = 'registration-grid-cell'
export const REGISTRATION_CELL_END_CLASS = 'registration-grid-cell-end'

const STATUS_FILTER_OPTIONS = REGISTRATION_STATUSES.map(
  ({ id, filterLabel }) => ({ label: filterLabel, value: id })
)

interface UseRegistrationColumnsArgs {
  registrations: UserRegistration[]
  isMobile: boolean
  onResumeSession: (record: UserRegistration) => void
  onDeleteSession: (record: UserRegistration) => void
}

/** The My Registrations columns. Only Name, Type and Reg.Date sort. */
export const useRegistrationColumns = ({
  registrations,
  isMobile,
  onResumeSession,
  onDeleteSession,
}: UseRegistrationColumnsArgs): ColDef<UserRegistration>[] => {
  'use memo'

  return [
    {
      colId: 'team_name',
      headerName: capitalizeWords('Name'),
      valueGetter: ({ data }) => data?.team?.name,
      comparator: compareRegistrationNames,
      cellRenderer: NameCell,
      cellRendererParams: { onResumeSession },
      minWidth: 220,
      flex: 2,
    },
    {
      colId: 'registration_type',
      // SN-8480 — a Team Rep's budget line reads "Team Rep", not the wire's "Player".
      headerName: capitalizeWords('Type'),
      valueGetter: ({ data }) =>
        data ? getPayableRegistrationTypeLabel(data) : '',
      comparator: (a: string, b: string) => a.localeCompare(b),
      width: 100,
    },
    {
      colId: 'schedule_name',
      headerName: capitalizeWords(isCanlan ? 'Season' : 'Competition'),
      valueGetter: ({ data }) => data?.schedule?.name,
      cellRenderer: CompetitionCell,
      sortable: false,
      filter: SetColumnFilter,
      filterParams: { options: getCompetitionFilterOptions(registrations) },
      minWidth: 260,
      flex: 3,
    },
    {
      colId: 'created_at',
      headerName: capitalizeWords('Reg.Date'),
      valueGetter: ({ data }) => data?.created_at,
      valueFormatter: ({ value }) =>
        value ? formatShortDate(value, false) : '',
      comparator: compareRegistrationDates,
      width: 130,
    },
    {
      colId: 'outstanding',
      headerName: capitalizeWords('Outstanding'),
      type: 'rightAligned',
      cellClass: [REGISTRATION_CELL_CLASS, REGISTRATION_CELL_END_CLASS],
      valueGetter: ({ data }) =>
        data
          ? formatRegistrationMoney(
              getOutstandingAmount(data),
              getRegistrationCountry(data)
            )
          : '',
      sortable: false,
      width: 130,
    },
    {
      colId: 'paid',
      headerName: capitalizeWords('Paid'),
      type: 'rightAligned',
      cellClass: [REGISTRATION_CELL_CLASS, REGISTRATION_CELL_END_CLASS],
      cellRenderer: PaidCell,
      sortable: false,
      width: 130,
    },
    {
      colId: 'paid_date',
      headerName: capitalizeWords('Paid Date'),
      cellRenderer: PaidDateCell,
      sortable: false,
      width: 150,
    },
    {
      colId: 'status_id',
      headerName: capitalizeWords('Status'),
      valueGetter: ({ data }) => data?.status_id,
      cellRenderer: StatusCell,
      sortable: false,
      filter: SetColumnFilter,
      filterParams: { options: STATUS_FILTER_OPTIONS },
      width: 170,
    },
    {
      colId: 'actions',
      headerName: '',
      cellRenderer: ActionsCell,
      cellRendererParams: { isMobile, onResumeSession, onDeleteSession },
      pinned: isMobile ? undefined : 'right',
      sortable: false,
      resizable: false,
      width: 120,
    },
  ]
}
